import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import type { AppState } from '@/store/store.ts';
import type { CheckTargetAccumulatorParams } from '@/store/targetAccumulator/api/tradeCapture/checkTargetAccumulator.ts';
import type {
  ChangedFields,
  CheckTargetAccumulatorBody,
  TradeCaptureAccumulatorLeg,
  TradeCaptureAccumulatorScheduleItemRequest,
} from '@/store/targetAccumulator/api/tradeCapture/request.model.ts';
import type { TcAccumulatorHedgeType } from '@/store/targetAccumulator/api/tradeCapture/response.model.ts';
import type { AccumulatorScheduleLine, HedgeType } from '@/store/targetAccumulator/state.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { removeUndefinedValues } from '@/utils/removeUndefinedValues.ts';

export function getCheckTargetAccumulatorBody(
  params: CheckTargetAccumulatorParams,
  state: AppState,
): CheckTargetAccumulatorBody {
  const currencyPair = params.currencyPair;
  const tradeCapture = state.targetAccumulator[params.tileId]?.tradeCapture;

  if (isNotDefined(tradeCapture)) {
    throw new Error('Trade capture data is required to check target accumulator');
  }

  return [
    {
      sessionId: tradeCapture.sessionId,
      tileId: params.tileId,
      productType: 'FxTargetAccumulator',
      request: {
        idVersion: tradeCapture.version + 1,
        changedFields: buildChangedFields(params, currencyPair),
      },
    },
  ];
}

function buildChangedFields(
  params: CheckTargetAccumulatorParams,
  currencyPair: string | undefined | null,
): CheckTargetAccumulatorBody[0]['request']['changedFields'] {
  const { hedgeType, hedgeCurrencyIndex, hedgeAmount, spotHedge } = params.properties ?? {};

  return {
    ...removeUndefinedValues({
      currencyPair: params.properties?.currencyPair,
      hedgeType: mapToTCHedgeType(hedgeType),
      hedgeAmountInCcy1String: mapHedgeAmountByCurrencyIndex(hedgeCurrencyIndex, hedgeAmount, 1),
      hedgeAmountInCcy2String: mapHedgeAmountByCurrencyIndex(hedgeCurrencyIndex, hedgeAmount, 2),
      hedgePriceString: spotHedge === null ? null : spotHedge?.toString(),
      markupCurrency: isDefined(params.properties?.markupCurrency) ? params.properties?.markupCurrency : undefined,
    } satisfies Partial<ChangedFields>),
    legs: {
      0: buildChangedFieldsLeg(params, currencyPair),
    },
  };
}

function buildChangedFieldsLeg(
  params: CheckTargetAccumulatorParams,
  currencyPair: string | undefined | null,
): TradeCaptureAccumulatorLeg {
  const {
    premiumDate,
    clientsWay,
    notional,
    amountMode,
    notionalCurrencyIndex,
    premiumTypeCurrencyIndex,
    leverage,
    leverageAmount,
    accuType,
    strike,
    targetProfitType,
    target,
    firstFixingDate,
    firstFixingTenor,
    lastFixingDate,
    lastFixingTenor,
    fixingFrequency,
    numberOfFixings,
    settlementMode,
    isCrossed,
    crossCurrency,
    fixingReference1,
    fixingReference2,
    cashSettlementCurrency,
    pivot,
    strikeDown,
    strikeUp,
    ekiTrigger,
    ekiDown,
    ekiUp,
    schedule,
  } = params.properties ?? {};

  return {
    ...removeUndefinedValues({
      productName: 'FxTargetAccumulator',
      premiumPaymentCurrency: mapToPriceCurrency(premiumTypeCurrencyIndex, currencyPair),
      premiumPaymentDateString: isDefined(premiumDate) ? premiumDate : undefined,
      side: clientsWay,
      amountString: notional === null ? '' : notional?.toString(),
      amountSplitType: amountMode,
      amountCurrency: mapToCurrency(notionalCurrencyIndex, currencyPair),
      leverageString: leverage?.toString(),
      leverageAmountString: leverageAmount?.toString(),
      accuType,
      strikeString: strike === null ? null : strike?.toString(),
      ekiString: ekiTrigger === null ? null : ekiTrigger?.toString(),
      ekiDownString: ekiDown === null ? null : ekiDown?.toString(),
      ekiUpString: ekiUp === null ? null : ekiUp?.toString(),
      strikeDownString: strikeDown === null ? null : strikeDown?.toString(),
      pivotString: pivot === null ? null : pivot?.toString(),
      strikeUpString: strikeUp === null ? null : strikeUp?.toString(),
      koPaymentConvention: targetProfitType,
      fixingFrequency: fixingFrequency,
      numberOfFixings,
      fixingReference1,
      fixingReference2,
      isCrossed: isCrossed,
      crossCurrency: crossCurrency,
      targetString: target?.toString(),
      firstFixingDateString: firstFixingDate || firstFixingTenor,
      schedule: mapToTradeCaptureSchedule(schedule),
      deliveryType: settlementMode,
      cashSettlementCurrency: cashSettlementCurrency,
      expiryDateString: lastFixingDate || lastFixingTenor,
    }),
  } as TradeCaptureAccumulatorLeg;
}

function mapToTCHedgeType(hedgeType: HedgeType | null | undefined): TcAccumulatorHedgeType | null | undefined {
  if (hedgeType === 'Live') {
    return 'None';
  }

  return hedgeType;
}

const mapToCurrency = (
  amountCurrency: CurrencyIndex | undefined | null,
  currencyPair: string | undefined | null,
): string | undefined => {
  if (isNotDefined(amountCurrency) || isNotDefined(currencyPair) || isEmpty(currencyPair)) {
    return undefined;
  }

  const currencies = currencyPair.split('/');
  return currencies[amountCurrency - 1];
};

const mapToPriceCurrency = (
  premiumPaymentCurrency: CurrencyIndex | null | undefined,
  currencyPair: string | null | undefined,
): string | undefined => {
  if (isNotDefined(currencyPair) || isNotDefined(premiumPaymentCurrency)) {
    return undefined;
  }

  const [currency1, currency2] = currencyPair.split('/');
  switch (premiumPaymentCurrency) {
    case 1:
      return currency1;
    case 2:
      return currency2;
    default:
      return undefined;
  }
};

function mapToTradeCaptureSchedule(
  schedule: Record<string, Partial<AccumulatorScheduleLine> | null> | null | undefined,
): Record<string, Partial<TradeCaptureAccumulatorScheduleItemRequest> | null> | null | undefined {
  if (schedule === undefined) {
    return undefined;
  }
  if (schedule === null) {
    return null;
  }

  const result = Object.entries(schedule).reduce(
    (acc, [key, leg]) => {
      acc[key] =
        leg === null
          ? null
          : {
              fixingDateString: leg.fixingDate,
              paymentDateString: isDefined(leg.paymentDate) ? leg.paymentDate : undefined,
              strikeString: leg.strike?.toString(),
              strikeDownString: leg.strikeDown?.toString(),
              stepDownString: leg.ekiDown?.toString(),
              stepUpString: leg.ekiUp?.toString(),
              strikeUpString: leg.strikeUp?.toString(),
              stepString: leg.pivot?.toString() ?? leg.ekiTrigger?.toString(),
              amountString: leg.amount?.toString(),
              leverageAmountString: leg.leverageAmount?.toString(),
            };
      return acc;
    },
    {} as Record<string, Partial<TradeCaptureAccumulatorScheduleItemRequest | null>>,
  );

  return isEmpty(Object.keys(result)) ? undefined : result;
}

function mapHedgeAmountByCurrencyIndex(
  hedgeCurrencyIndex: CurrencyIndex | null | undefined,
  hedgeAmount: number | null | undefined,
  targetIndex: CurrencyIndex,
): string | null | undefined {
  if (hedgeCurrencyIndex === targetIndex) {
    return hedgeAmount === null ? null : hedgeAmount?.toString();
  }
  return undefined;
}
